import React, { useEffect, useState, useContext } from 'react';
import axios from '../axiosInstance';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { exportBrandedPdf } from '../utils/brandedPdf';
import { UserContext } from '../UserContext';
import '../Pages.css';
import Pagination from '../components/Pagination';
import './PipSearchHistory.css';
import { formatDateTime } from '../utils/dateFormat';

export default function PipSearchHistory() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  // Helper to pretty-print JSON or fallback to string
  const prettyResult = (result) => {
    try {
      return JSON.stringify(JSON.parse(result), null, 2);
    } catch {
      return result;
    }
  };
  const { user } = useContext(UserContext);
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [allOrgs, setAllOrgs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [orgIdForApi, setOrgIdForApi] = useState('');

  // Initial setup - runs once when component mounts
  useEffect(() => {
    if (user) {
      // Fetch organization data for all users
      fetchOrgs();
      
      // For non-admin users without org ID, fetch complete user details
      if (!user.is_system_admin && !user.organisation_id) {
        fetchUserDetails();
      }
      
      // If user already has organization ID, set it
      if (user.organisation_id) {
        setSelectedOrg(user.organisation_id);
        setOrgIdForApi(user.organisation_id);
      }
      
      // Ensure we fetch logs even if there's no organization ID yet
      // This will allow the backend to determine what the user can see
      if (!user.organisation_id && !user.is_system_admin) {
        fetchLogs('');
      }
    }
    // eslint-disable-next-line
  }, [user]);
  
  // Function to fetch complete user details including organization ID
  const fetchUserDetails = async () => {
    try {
      const res = await axios.get('/users/me');
      
      if (res.data && res.data.organisation_id) {
        setSelectedOrg(res.data.organisation_id);
        setOrgIdForApi(res.data.organisation_id);
        
        // Update local storage with complete user data
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, organisation_id: res.data.organisation_id };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (e) {
      console.error('Error fetching user details:', e);
    }
  };
  
  // When organizations are loaded or user selects an organization
  useEffect(() => {
    // If we have orgs and user is admin without a selection, select the first org
    if (user?.is_system_admin && allOrgs.length > 0 && !selectedOrg) {
      setSelectedOrg(allOrgs[0]?.id || '');
      setOrgIdForApi(allOrgs[0]?.id || '');
    } else if (!user?.is_system_admin && allOrgs.length > 0 && user?.organisation_id) {
      // For regular users, find their organization in the list and select it
      const userOrg = allOrgs.find(org => org.id === user.organisation_id);
      if (userOrg) {
        setSelectedOrg(userOrg.id);
        setOrgIdForApi(userOrg.id);
      }
    }
    // eslint-disable-next-line
  }, [allOrgs]);
  
  // When selected org changes, update the API org ID
  useEffect(() => {
    if (selectedOrg) {
      setOrgIdForApi(selectedOrg);
    }
  }, [selectedOrg]);
  
  // Data fetching based on organization ID for API
  useEffect(() => {
    if (orgIdForApi) {
      fetchLogs(orgIdForApi);
      fetchDashboard(orgIdForApi);
    } else {
      // Admin with no org selected - just fetch logs for all orgs
      if (user?.is_system_admin) {
        fetchLogs('');
      }
    }
    // eslint-disable-next-line
  }, [orgIdForApi]);
  // Update the useEffect that fetches dashboard data
  useEffect(() => {
    if (user) {
      const userOrgId = user.organisation_id;
      if (userOrgId) {
        setOrgIdForApi(userOrgId);
        if (!user.is_system_admin) {
          fetchDashboard(userOrgId);
        }
      }
    }
  }, [user]);

  // Update another useEffect that depends on orgIdForApi
  useEffect(() => {
    if (!user) return;
    if (!orgIdForApi) {
      setDashboard(null);
      return;
    }
    fetchDashboard(orgIdForApi);
  }, [orgIdForApi, user]);

  // Update the fetchDashboard function to ensure proper data mapping
  const fetchDashboard = (orgId) => {
    if (!orgId) return;
    
    axios.get(`/audit/pip-searches/dashboard?organisation_id=${orgId}`)
      .then(response => {
        const data = response.data;
        
        // Map the response data to match the expected dashboard structure
        setDashboard({
          organisation: data.organisation,
          package: data.package,
          user_limit: data.user_limit,
          user_count: data.user_count,
          screening_limit: data.screening_limit,
          screenings_done: data.screenings_done,
          screenings_left: data.screenings_left,
          batch_screening_limit: data.batch_screening_limit,
          batch_once_off_limit: data.batch_once_off_limit,
          batch_once_off_left: data.batch_once_off_left,
          batch_monthly_limit: data.batch_monthly_limit,
          batch_monthly_left: data.batch_monthly_left,
          batch_screenings_this_month: data.batch_screenings_this_month,
          batch_screenings_done: data.batch_screenings_done || 0,
          batch_screenings_left: data.batch_screenings_left || (data.batch_screening_limit === null ? 'Unlimited' : 0)
        });
      })
      .catch(err => {
        console.error('Failed to fetch PIP search dashboard:', err);
        setDashboard(null);
      });
  };

  const fetchOrgs = async () => {
    try {
      const res = await axios.get('/organisations');
      const orgs = res.data || [];
      setAllOrgs(orgs);
      
      // For admin users: if we have orgs but no selection yet, select the first one by default
      if (user?.is_system_admin && orgs.length > 0 && !selectedOrg) {
        setSelectedOrg(orgs[0]?.id || '');
        setOrgIdForApi(orgs[0]?.id || '');
      }
      
      // For regular users: find and select their organization
      if (!user?.is_system_admin && user?.organisation_id && orgs.length > 0) {
        const userOrg = orgs.find(org => org.id === user.organisation_id);
        if (userOrg) {
          setSelectedOrg(userOrg.id);
          setOrgIdForApi(userOrg.id);
        }
      }
    } catch (e) {
  console.error('Error fetching organisations:', e);
      setAllOrgs([]);
    }
  };

  const fetchLogs = async (orgId = '') => {
    setLoading(true);
    setError('');
    
    try {
      let url = '/audit/pip-searches';
      if (orgId) url += `?organisation_id=${orgId}`;
      
      const res = await axios.get(url);
      
      setLogs(res.data || []);
      // Clear any previous errors if successful
      setError('');
    } catch (e) {
      console.error('Error fetching logs:', e);
      setLogs([]);
      
      if (e.response?.status === 401) {
        setError('You need to log in to view search history');
      } else if (e.response?.status === 403) {
        setError(e.response?.data?.error || 'You do not have permission to view search history');
      } else {
        setError(e.response?.data?.error || e.message || 'Failed to fetch PIP search history');
      }
    }
    
    setLoading(false);
  };

  // Export functions
  // Filtering and sorting (no sort for now, but can add if needed)
  const filtered = searchTerm
    ? logs.filter(l =>
        Object.values(l).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : logs;
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentLogs = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const exportData = filtered.map(log => ({
    Date: formatDateTime(log.searched_at),
    User: log.user_email,
    Query: log.search_query,
    Result: log.search_result
  }));

  const exportExcel = () => {
    // Create metadata sheet with user details
    const metadataWs = XLSX.utils.json_to_sheet([
      { 'Key': 'Export Date', 'Value': formatDateTime(new Date()) },
      { 'Key': 'Exported By', 'Value': user ? `${user.first_name || user.name || ''} ${user.last_name || user.surname || ''}`.trim() || 'Unknown User' : 'Unknown User' },
      { 'Key': 'User ID', 'Value': user?.id || 'Unknown' },
  { 'Key': 'Organisation', 'Value': (user?.organisation_name || user?.organization_name || 'N/A') },
      { 'Key': 'Email', 'Value': user?.email || 'N/A' },
  { 'Key': 'Selected Organisation', 'Value': selectedOrg || 'All' },
      { 'Key': 'Search Term', 'Value': searchTerm || 'All' },
      { 'Key': 'Results Count', 'Value': exportData.length }
    ]);
    
    // Create data sheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Create workbook with both sheets
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, metadataWs, 'Export Info');
    XLSX.utils.book_append_sheet(wb, ws, 'PIP Search History');
    
    // Generate filename with date
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    XLSX.writeFile(wb, `pip_search_history_${dateStr}.xlsx`);
  };

  const exportPDF = () => exportBrandedPdf({
    title: 'PIP SEARCH HISTORY — REPORT',
    user,
    searchValue: [
      searchTerm && `"${searchTerm}"`,
      selectedOrg && `Organisation: ${selectedOrg}`
    ].filter(Boolean).join('  ·  ') || 'All searches',
    fileName: 'PIP_Search_History_Report',
    columns: [
      { header: 'Date', width: 16 }, { header: 'User', width: 22 },
      { header: 'Query', width: 40, bold: true }, { header: 'Result', width: 30 }
    ],
    rows: exportData.map(row => Object.values(row))
  });

  // Update document title when dashboard data is loaded
  useEffect(() => {
    document.title = dashboard ? 'PIP Search History | Dashboard' : 'PIP Search History';
  }, [dashboard]);

  return (
    <div className="page-container">
      {/* Dashboard section - always show this container */}
      <div className="stats-section">
        <div className="stats-section-header">
          <h2>Package Statistics</h2>
          {orgIdForApi && allOrgs.length > 0 && (
            <span className="stats-section-badge">
              {allOrgs.find(org => org.id === parseInt(orgIdForApi))?.name || 'Organisation'}
            </span>
          )}
        </div>

        {/* Show loading or error message when appropriate */}
        {!dashboard && (
          <div className="info-panel">
            {!user ? (
              <>
                <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading user information...</div>
                <div className="loader"></div>
              </>
            ) : !user.is_system_admin && !user.organisation_id ? (
              <>
                <div className="info-panel__title info-panel__title--error">Missing Organisation Association</div>
                <div className="info-panel__body">Your user account is not associated with any organisation.</div>
                <div className="info-panel__body">Please contact your administrator.</div>
              </>
            ) : !orgIdForApi ? (
              <>
                <div className="info-panel__title">No Organisation Selected</div>
                {user?.is_system_admin && allOrgs.length > 0 && (
                  <div className="info-panel__body">Please select an organisation from the dropdown above</div>
                )}
                {user?.is_system_admin && allOrgs.length === 0 && (
                  <div className="info-panel__body">Loading organisations...</div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading Dashboard Data</div>
                <div className="loader"></div>
                <div className="info-panel__body">If this persists, try selecting a different organisation</div>
              </>
            )}
          </div>
        )}

        {/* Dashboard cards showing package information and search statistics */}
        {dashboard && (
          <>
            {/* Shared stat-card layout (Pips.css): compact cards */}
            <div className="stats-layout__rest">
            {/* First row - Package and Single Search Info */}
            <div className="stats-grid">
              <div className="stat-card stat-card--teal">
                <div className="stat-label">Package Name</div>
                <div className="stat-value stat-value--teal">{dashboard.package}</div>
              </div>
              <div className="stat-card stat-card--blue">
                <div className="stat-label">Single Screening Limit</div>
                <div className="stat-value stat-value--blue">
                  {dashboard.screening_limit === null ? 'Unlimited' : dashboard.screening_limit}
                </div>
              </div>
              <div className="stat-card stat-card--orange">
                <div className="stat-label">Single Screenings Done</div>
                <div className="stat-value stat-value--orange">{dashboard.screenings_done}</div>
              </div>
              <div className="stat-card stat-card--teal">
                <div className="stat-label">Single Screenings Left</div>
                <div className={`stat-value ${dashboard.screenings_left === 0 && dashboard.screening_limit !== null ? 'stat-value--red' : 'stat-value--green'}`}>
                  {dashboard.screenings_left === 'Unlimited' ? 'Unlimited' : dashboard.screenings_left}
                </div>
              </div>
            </div>

            {/* Second row - Bulk Search Info */}
            <div className="stats-grid">
              <div className="stat-card stat-card--purple">
                <div className="stat-label">Bulk Screening Limit</div>
                <div className="stat-value stat-value--purple">
                  {dashboard.batch_screening_limit === null ? 'Unlimited' : dashboard.batch_screening_limit}
                </div>
                {dashboard.batch_screening_limit !== null && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)', marginTop: '0.35rem' }}>
                    {dashboard.batch_monthly_limit ?? 0} monthly + {dashboard.batch_once_off_limit ?? 0} once-off
                  </div>
                )}
              </div>
              <div className="stat-card stat-card--orange">
                <div className="stat-label">Bulk Screenings Done</div>
                <div className="stat-value stat-value--orange">{dashboard.batch_screenings_done || 0}</div>
              </div>
              <div className="stat-card stat-card--teal">
                <div className="stat-label">Bulk Screenings Left</div>
                <div className={`stat-value ${
                  dashboard.batch_screenings_left === 0 && dashboard.batch_screening_limit !== null ? 'stat-value--red' :
                  dashboard.batch_screenings_left <= 5 && dashboard.batch_screening_limit !== null ? 'stat-value--orange' : 'stat-value--green'
                }`}>
                  {dashboard.batch_screenings_left === 'Unlimited' ? 'Unlimited' : dashboard.batch_screenings_left}
                </div>
                {dashboard.batch_screening_limit !== null && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)', marginTop: '0.35rem' }}>
                    {dashboard.batch_monthly_left ?? 0} this month + {dashboard.batch_once_off_left ?? 0} once-off
                    {' '}· {dashboard.batch_screenings_this_month || 0} used this month
                  </div>
                )}
              </div>
              <div className="stat-card stat-card--brown">
                <div className="stat-label">Total Searches</div>
                <div className="stat-value stat-value--teal">
                  {(dashboard.screenings_done || 0) + (dashboard.batch_screenings_done || 0)}
                </div>
              </div>
            </div>
            </div>

            {/* Warning messages */}
            {dashboard.screenings_left === 0 && dashboard.screening_limit !== null && (
              <div className="stat-limit-warning">
                <strong>Warning:</strong> This organisation has reached the maximum number of single searches allowed by their package.
                Users will not be able to perform additional single searches until the package is upgraded.
              </div>
            )}
            {dashboard.batch_screenings_left === 0 && dashboard.batch_screening_limit !== null && (
              <div className="stat-limit-warning">
                <strong>Warning:</strong> This organisation has reached the maximum number of bulk searches allowed by their package.
                Users will not be able to perform additional bulk searches until the package is upgraded.
              </div>
            )}
            {dashboard.batch_screenings_left > 0 && dashboard.batch_screenings_left <= 5 && dashboard.batch_screening_limit !== null && (
              <div className="stat-limit-caution">
                <strong>Notice:</strong> This organisation has only {dashboard.batch_screenings_left} bulk search{dashboard.batch_screenings_left === 1 ? '' : 'es'} remaining.
              </div>
            )}
          </>
        )}
      </div>
      
      <div className="table-controls">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="text"
            placeholder="Search history..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="search-filter"
          />
          {user?.is_system_admin && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label htmlFor="org-select" style={{ marginRight: 8, whiteSpace: 'nowrap' }}>Organisation:</label>
              <select
                id="org-select"
                value={selectedOrg || ''}
                onChange={e => {
                  const newOrgId = e.target.value;
                  
                  // Don't allow clearing the selection - if empty, select the first org
                  if (!newOrgId && allOrgs.length > 0) {
                    const defaultOrgId = allOrgs[0]?.id;
                    setSelectedOrg(defaultOrgId);
                    setOrgIdForApi(defaultOrgId);
                  } else {
                    setSelectedOrg(newOrgId);
                    setOrgIdForApi(newOrgId);
                  }
                }}
                className="org-select"
              >
                {allOrgs.length === 0 ? (
                  <option value="">Loading organisations...</option>
                ) : (
                  allOrgs.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))
                )}
              </select>
            </div>
          )}
        </div>
        <div className="button-group">
          <CSVLink 
            filename={`pip_search_history_${new Date().toISOString().split('T')[0]}.csv`} 
            data={[
              // Metadata rows
              { Date: 'EXPORT INFORMATION', User: '', Query: '', Result: '' },
              { Date: 'Export Date', User: formatDateTime(new Date()), Query: '', Result: '' },
              { 
                Date: 'Exported By',
                User: user
                  ? `${user.first_name || user.name || ''} ${user.last_name || user.surname || ''}`.trim() || 'Unknown User'
                  : `Unknown User (ID: ${user?.id || 'Unknown'})`, // <-- FIXED: use template literal, not concatenation
                Query: '', 
                Result: '' 
              },
              { 
                Date: 'Organisation', 
                User: user?.organisation_name || user?.organization_name || 'N/A', 
                Query: '', 
                Result: '' 
              },
              { Date: 'Email', User: user?.email || 'N/A', Query: '', Result: '' },
              { Date: 'Selected Organisation', User: selectedOrg || 'All', Query: '', Result: '' },
              { Date: 'Search Term', User: searchTerm || 'All', Query: '', Result: '' },
              { Date: 'Total Records', User: exportData.length.toString(), Query: '', Result: '' },
              { Date: '', User: '', Query: '', Result: '' }, // Empty row as separator
              { Date: 'SEARCH HISTORY DATA', User: '', Query: '', Result: '' },
              ...exportData
            ]}
            className="export-button">Export CSV</CSVLink>
          <button className="export-button" onClick={exportExcel}>Export Excel</button>
          <button className="export-button" onClick={exportPDF}>Export PDF</button>
        </div>
      </div>
      
      <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
        Showing {currentLogs.length} of {filtered.length} search records {searchTerm ? `(filtered from ${logs.length})` : ''}
      </div>
      
      <div className="table-container">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="loading-spinner"></div>
            <div>Loading search history...</div>
          </div>
        ) : error ? (
          <div className="stat-limit-warning" style={{ textAlign: 'center', margin: '1rem', padding: '2rem' }}>
            <h3>Access Error</h3>
            <p>{error}</p>
            {(!user || !user.id) && (
              <p>Please make sure you are properly logged in and try again.</p>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>No search history found.</div>
        ) : (
          <table className="pips-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>User</th>
                <th>Query</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {currentLogs.map((log, idx) => {
                const preview = log.search_result && log.search_result.length > 40
                  ? log.search_result.slice(0, 40) + '...'
                  : log.search_result;
                return (
                  <tr key={log.id}>
                    <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td>{formatDateTime(log.searched_at)}</td>
                    <td>{log.user_email}</td>
                    <td>{log.search_query}</td>
                    <td>
                      <pre style={{ maxWidth: 300, whiteSpace: 'pre-wrap', display: 'inline' }}>{preview}</pre>
                      {log.search_result && log.search_result.length > 100 && (
                        <button
                          className="view-more-btn"
                          onClick={() => { setModalContent(prettyResult(log.search_result)); setModalOpen(true); }}
                          style={{ marginLeft: 8 }}
                        >
                          View More
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={filtered.length}
        itemsPerPage={itemsPerPage}
        itemLabel="search"
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
      />
      {/* Modal for full result */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h4>Full Result</h4>
            <pre className="pre-result">{modalContent}</pre>
            <button onClick={() => setModalOpen(false)} className="export-button" style={{ marginTop: 12 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
